// Shared pieces for the P5-C admin screens: the store-scoped page frame (permission gate), status
// badges (tone + icon + text, never colour alone) and money. Copy comes from `commerce.*` strings.
import type { ReactNode } from "react";
import { formatMoney } from "@ady/format";
import { i18n, t } from "@ady/i18n";
import {
  Badge,
  BrandIcon,
  brandFor,
  cx,
  Icon,
  StatePanel,
  type IconName,
  type Tone,
} from "@ady/ui";
import { useShell } from "../shell/context";
import { can } from "../shell/nav";
import type { OrderStatus, PaymentStatus } from "./api";
import { PageHeader } from "../shell/PageHeader";

export function useStoreIds(): {
  orgId: string;
  storeId: string;
  orgSlug: string;
  storeSlug: string;
} {
  const { membership, store, orgSlug, storeSlug } = useShell();
  return { orgId: membership.org.id, storeId: store.id, orgSlug, storeSlug };
}

export function useCan(...anyOf: string[]): boolean {
  const { membership } = useShell();
  return can(membership, anyOf);
}

export function CommercePage({
  title,
  lead,
  anyOf,
  testId,
  canonical,
  actions,
  wide = true,
  above,
  children,
}: {
  /** Rendered above the title (a section switcher, as on Reports). */
  above?: ReactNode;
  title: string;
  lead?: ReactNode;
  anyOf: string[];
  testId: string;
  canonical: string;
  actions?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  const allowed = useCan(...anyOf);
  if (!allowed) {
    return (
      <StatePanel
        kind="permission-denied"
        size="page"
        headingLevel={2}
        title={t("states.permissionDeniedTitle")}
        body={t("states.permissionDeniedBody", { who: t("states.permissionWhoOwner") })}
        data-testid={testId}
      />
    );
  }
  return (
    <div
      className={wide ? "flex w-full flex-col gap-4" : "flex w-full max-w-3xl flex-col gap-4"}
      data-testid={testId}
      data-state="ready"
      data-canonical={canonical}
    >
      {above}
      <PageHeader title={title} lead={lead} actions={actions} />
      {children}
    </div>
  );
}

export function Card({
  title,
  aside,
  children,
  id,
}: {
  title: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  id: string;
}) {
  return (
    <section
      className="flex min-w-0 flex-col gap-3 rounded-xs border border-card bg-surface p-4 sm:p-5"
      aria-labelledby={`${id}-h`}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={`${id}-h`} className="text-md font-bold">
          {title}
        </h2>
        {aside ? <span className="text-sm text-fg-secondary">{aside}</span> : null}
      </header>
      {children}
    </section>
  );
}

const ORDER_TONE: Record<OrderStatus, Tone> = {
  draft: "neutral",
  pending: "action",
  confirmed: "action",
  processing: "action",
  ready: "action",
  shipped: "transit",
  delivered: "success",
  completed: "success",
  cancelled: "neutral",
  returned: "danger",
};
const PAYMENT_TONE: Record<PaymentStatus, Tone> = {
  unpaid: "neutral",
  partial: "warning",
  paid: "success",
  refunded: "neutral",
  partially_refunded: "neutral",
  awaiting_verification: "action",
  overpaid: "warning",
};

export const orderStatusLabel = (s: OrderStatus): string => t(`commerce.status.order.${s}`);
export const paymentStatusLabel = (s: PaymentStatus): string => t(`commerce.status.payment.${s}`);
export const methodLabel = (m: string): string =>
  i18n.tDynamic(`commerce.status.method.${m}`, "commerce.status.method.other");

/** Our icon for methods without a brand mark. */
const METHOD_ICON: Record<string, IconName> = {
  cash: "money",
  cod: "cash",
  bank_transfer: "bank",
  card: "card",
  wallet: "wallet",
  cheque: "file",
  manual_qr: "qr",
  qr: "qr",
  merchant_qr: "qr",
};

/** COD and QR, the two everyday methods, read as tags at a glance (owner, 2026-10-21). */
const METHOD_TAG: Record<string, string> = {
  cod: "border-success-border bg-success-bg text-success-fg",
  manual_qr: "border-action-border bg-action-bg text-action-fg",
  qr: "border-action-border bg-action-bg text-action-fg",
  merchant_qr: "border-action-border bg-action-bg text-action-fg",
};

/** Just the method's mark (brand mark or our icon), e.g. inside a filter chip. */
export function MethodMark({ method }: { method: string }) {
  const brand = brandFor(method.replace(/_qr$/, ""));
  const icon = METHOD_ICON[method];
  if (brand) return <BrandIcon brand={brand} size={16} className="flex-none" />;
  return icon ? <Icon name={icon} className="flex-none" /> : null;
}

/** A payment method with its mark: the logo for eSewa, Khalti, Fonepay, Stripe (D-040), ours otherwise. */
export function MethodLabel({ method }: { method: string }) {
  const brand = brandFor(method.replace(/_qr$/, ""));
  const icon = METHOD_ICON[method];
  const tag = METHOD_TAG[method];
  if (tag && icon)
    return (
      <span
        className={cx(
          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold",
          tag,
        )}
      >
        {brand ? (
          <BrandIcon brand={brand} size={16} className="flex-none" />
        ) : (
          <Icon name={icon} className="flex-none" />
        )}
        {methodLabel(method)}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-2">
      {brand ? (
        <BrandIcon brand={brand} size={16} className="flex-none" />
      ) : icon ? (
        <Icon name={icon} size={16} className="flex-none text-fg-tertiary" />
      ) : null}
      {methodLabel(method)}
    </span>
  );
}

export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  return <Badge tone={ORDER_TONE[status]}>{orderStatusLabel(status)}</Badge>;
}
export function PaymentStatusBadge({ status }: { status: PaymentStatus }) {
  return <Badge tone={PAYMENT_TONE[status]}>{paymentStatusLabel(status)}</Badge>;
}

export const money = (paisa: number): string => formatMoney(paisa);

export function Money({ paisa, strong = false }: { paisa: number; strong?: boolean }) {
  return <span className={strong ? "ady-ledger font-semibold" : "ady-ledger"}>{money(paisa)}</span>;
}
